import type { AppThunk } from 'typesafe-actions';
import { createAsyncAction, createStandardAction } from 'typesafe-actions';

import generateLogger from '@travel/logger';

import {
  getFilterAPI,
  getPlansListAPI,
  getProvidersListAPI,
  postAddFavoriteAPI,
  postRemoveFavoriteAPI,
} from './api';

import type { AddFavoriteRequestBody, RemoveFavoriteRequestBody } from 'Favorite-Types';
import type { PlanListWithArrayIndex, PlanListWithProviderId } from 'Plan-Types';
import type { ProviderWithArrayIndex } from 'Provider-Types';
import type { PlanResponse, ProviderResponse, RequestBody } from 'ProviderPlan-Types';
import { ServiceType } from 'favoriteList/constants/favoriteLists';
import { OVERSEAS_PROVIDER_FILTER } from 'favoriteList/constants/overseasProviderFilter';
import { checkIsNumber } from './helper';

export const setCurrentProviderDataCount = createStandardAction(
  'SET_CURRENT_PROVIDER_DATA_COUNT',
)<number>();

export const setCurrentPlanDataCount = createStandardAction(
  'SET_CURRENT_PLAN_DATA_COUNT',
)<number>();

export const setDraftProvider =
  createStandardAction('SET_DRAFT_PROVIDER')<ProviderWithArrayIndex>();
export const setCurrentProviderListStatus = createStandardAction(
  'SET_CURRENT_PROVIDER_LIST_STATUS',
)<ProviderResponse>();

export const setDraftPlan = createStandardAction('SET_DRAFT_PLAN')<PlanListWithProviderId>();
export const setCurrentPlanListStatus = createStandardAction(
  'SET_CURRENT_PLAN_LIST_STATUS',
)<PlanResponse>();

export const setIsFavoriteHandlerSuccess = createStandardAction(
  'SET_IS_FAVORITE_HANDLER_SUCCESS',
)<boolean>();

const logger = generateLogger();
export const fetchProvidersListAsync = createAsyncAction(
  'FETCH_PROVIDER_LIST_REQUEST',
  'FETCH_PROVIDER_LIST_SUCCESS',
  'FETCH_PROVIDER_LIST_FAILURE',
)<undefined, any, any>();

export const fetchPlansListAsync = createAsyncAction(
  'FETCH_PLAN_LIST_REQUEST',
  'FETCH_PLAN_LIST_SUCCESS',
  'FETCH_PLAN_LIST_FAILURE',
)<undefined, any, any>();

export const fetchMoreProvidersListAsync = createAsyncAction(
  'FETCH_MORE_PROVIDER_LIST_REQUEST',
  'FETCH_MORE_PROVIDER_LIST_SUCCESS',
  'FETCH_MORE_PROVIDER_LIST_FAILURE',
)<undefined, any, any>();

export const fetchMorePlansListAsync = createAsyncAction(
  'FETCH_MORE_PLAN_LIST_REQUEST',
  'FETCH_MORE_PLAN_LIST_SUCCESS',
  'FETCH_MORE_PLAN_LIST_FAILURE',
)<undefined, any, any>();

export const fetchFilterAsync = createAsyncAction(
  'FETCH_FILTER_REQUEST',
  'FETCH_FILTER_SUCCESS',
  'FETCH_FILTER_FAILURE',
)<undefined, any, any>();

export const fetchProviderList =
  (params: RequestBody): AppThunk =>
  async (dispatch, _getState, { apiClient }) => {
    dispatch(fetchProvidersListAsync.request());
    try {
      const response = await getProvidersListAPI(apiClient, {
        ...params,
      });
      dispatch(setCurrentProviderDataCount(response?.body?.total || 0));
      dispatch(fetchProvidersListAsync.success(response));
    } catch (error) {
      logger.error(error);
      dispatch(fetchProvidersListAsync.failure(error));
    }
  };

export const fetchPlanList =
  (params: RequestBody): AppThunk =>
  async (dispatch, _getState, { apiClient }) => {
    dispatch(fetchPlansListAsync.request());
    try {
      const response = await getPlansListAPI(apiClient, {
        ...params,
      });
      dispatch(setCurrentPlanDataCount(response?.body?.total || 0));
      dispatch(fetchPlansListAsync.success(response));
    } catch (error) {
      logger.error(error);
      dispatch(fetchPlansListAsync.failure(error));
    }
  };

export const fetchMoreProviderList =
  (params: RequestBody): AppThunk =>
  async (dispatch, _getState, { apiClient }) => {
    dispatch(fetchMoreProvidersListAsync.request());
    try {
      const response = await getProvidersListAPI(apiClient, {
        ...params,
      });
      dispatch(setCurrentProviderDataCount(response?.body?.total || 0));
      dispatch(fetchMoreProvidersListAsync.success(response));
    } catch (error) {
      logger.error(error);
      dispatch(fetchMoreProvidersListAsync.failure(error));
    }
  };

export const fetchMorePlanList =
  (params: RequestBody): AppThunk =>
  async (dispatch, _getState, { apiClient }) => {
    dispatch(fetchMorePlansListAsync.request());
    try {
      const response = await getPlansListAPI(apiClient, {
        ...params,
      });
      dispatch(setCurrentPlanDataCount(response?.body?.total || 0));
      dispatch(fetchMorePlansListAsync.success(response));
    } catch (error) {
      logger.error(error);
      dispatch(fetchMorePlansListAsync.failure(error));
    }
  };

export const removeFavoriteToProvider =
  (draftProvider: ProviderWithArrayIndex): AppThunk =>
  async (dispatch, getState, { apiClient }) => {
    const { providerPlan } = getState();

    const { providerList, providerDataCount } = providerPlan;
    const provider = providerList.body.providers.find(
      (provider) => provider.hotelNo === draftProvider.hotelNo,
    );
    if (provider) {
      const params: RemoveFavoriteRequestBody = {
        providerId: provider.hotelNo.toString(),
      };
      try {
        await postRemoveFavoriteAPI(apiClient, {
          ...params,
        });

        const updatedProviderList: ProviderResponse = {
          ...providerList,
          body: {
            ...providerList.body,
            providers: providerList.body.providers.filter(
              (provider) => provider.hotelNo !== draftProvider.hotelNo,
            ),
          },
        };

        dispatch(setCurrentProviderListStatus(updatedProviderList));
        dispatch(setCurrentProviderDataCount(providerDataCount - 1));

        dispatch(setDraftProvider(draftProvider));
        dispatch(setIsFavoriteHandlerSuccess(true));
      } catch (error) {
        logger.error(error);
        dispatch(setIsFavoriteHandlerSuccess(false));
      }
    }
  };

export const addFavoriteToProvider =
  (isDomestic: boolean): AppThunk =>
  async (dispatch, getState, { apiClient }) => {
    const { providerPlan } = getState();

    const { providerList, draftProvider, providerDataCount } = providerPlan;

    checkIsNumber({
      value: draftProvider.hotelNo,
      errorHandler: {
        dispatch,
        payloadAction: setIsFavoriteHandlerSuccess,
      },
    });

    const params: AddFavoriteRequestBody = {
      serviceType: isDomestic ? ServiceType.DOMESTIC : ServiceType.OVERSEAS_HOTEL,
      providerId: draftProvider.hotelNo.toString(),
      bookmarkedDate: draftProvider.bookmarkedDate,
    };
    try {
      await postAddFavoriteAPI(apiClient, {
        ...params,
      });

      const updatedProviderList: ProviderResponse = {
        ...providerList,
        body: {
          ...providerList.body,
          providers: [
            ...providerList.body.providers.slice(0, draftProvider.arrayIndex),
            draftProvider,
            ...providerList.body.providers.slice(draftProvider.arrayIndex),
          ],
        },
      };
      dispatch(setCurrentProviderListStatus(updatedProviderList));
      dispatch(setCurrentProviderDataCount(providerDataCount + 1));

      dispatch(setDraftProvider({} as ProviderWithArrayIndex));
      dispatch(setIsFavoriteHandlerSuccess(true));
    } catch (error) {
      logger.error(error);
      dispatch(setIsFavoriteHandlerSuccess(false));
    }
  };

export const removeFavoriteToPlan =
  (draftPlan: PlanListWithArrayIndex): AppThunk =>
  async (dispatch, getState, { apiClient }) => {
    const { providerPlan } = getState();

    const { planList, planDataCount } = providerPlan;
    const { body } = planList;
    const { providerPlanList } = body;
    const targetProviderPlan = providerPlanList.find((providerPlan) =>
      providerPlan.planList.some((item) => item.id === draftPlan.id),
    );
    if (targetProviderPlan) {
      const draftPlanWithProviderId = {
        ...draftPlan,
        providerId: targetProviderPlan.provider.hotelNo,
      };
      const params: RemoveFavoriteRequestBody = {
        providerId: draftPlanWithProviderId.providerId.toString(),
        planId: draftPlanWithProviderId.id.toString(),
      };
      try {
        await postRemoveFavoriteAPI(apiClient, {
          ...params,
        });

        const updatedPlanList: PlanResponse = {
          ...planList,
          body: {
            ...planList.body,
            providerPlanList: providerPlanList.map((providerPlan) => {
              if (providerPlan.provider.hotelNo === targetProviderPlan.provider.hotelNo) {
                return {
                  ...providerPlan,
                  planList: providerPlan.planList.filter((item) => item.id !== draftPlan.id),
                };
              }
              return providerPlan;
            }),
          },
        };
        dispatch(setCurrentPlanListStatus(updatedPlanList));
        dispatch(setCurrentPlanDataCount(planDataCount - 1));

        dispatch(setDraftPlan(draftPlanWithProviderId));
        dispatch(setIsFavoriteHandlerSuccess(true));
      } catch (error) {
        logger.error(error);
        dispatch(setIsFavoriteHandlerSuccess(false));
      }
    }
  };

export const addFavoriteToPlan =
  (isDomestic: boolean): AppThunk =>
  async (dispatch, getState, { apiClient }) => {
    const { providerPlan } = getState();

    const { planList, draftPlan, planDataCount } = providerPlan;

    checkIsNumber({
      value: draftPlan.providerId,
      errorHandler: {
        dispatch,
        payloadAction: setIsFavoriteHandlerSuccess,
      },
    });

    checkIsNumber({
      value: draftPlan.id,
      errorHandler: {
        dispatch,
        payloadAction: setIsFavoriteHandlerSuccess,
      },
    });

    const params: AddFavoriteRequestBody = {
      serviceType: isDomestic ? ServiceType.DOMESTIC : ServiceType.OVERSEAS_HOTEL,
      providerId: draftPlan.providerId.toString(),
      planId: draftPlan.id.toString(),
      bookmarkedDate: draftPlan.bookmarkedDate,
    };
    try {
      await postAddFavoriteAPI(apiClient, {
        ...params,
      });

      const updatedPlanList: PlanResponse = {
        ...planList,
        body: {
          ...planList.body,
          providerPlanList: planList.body.providerPlanList.map((providerPlan) => {
            if (providerPlan.provider.hotelNo === draftPlan.providerId) {
              return {
                ...providerPlan,
                planList: [
                  ...providerPlan.planList.slice(0, draftPlan.arrayIndex),
                  draftPlan,
                  ...providerPlan.planList.slice(draftPlan.arrayIndex),
                ],
              };
            }
            return providerPlan;
          }),
        },
      };
      dispatch(setCurrentPlanListStatus(updatedPlanList));
      dispatch(setCurrentPlanDataCount(planDataCount + 1));

      dispatch(setDraftPlan({} as PlanListWithProviderId));
      dispatch(setIsFavoriteHandlerSuccess(true));
    } catch (error) {
      logger.error(error);
      dispatch(setIsFavoriteHandlerSuccess(false));
    }
  };

export const fetchFilter =
  (isPlanList: boolean, isDomestic: boolean): AppThunk =>
  async (dispatch, _getState, { apiClient }) => {
    if (!isDomestic) {
      dispatch(fetchFilterAsync.success(isPlanList ? [] : OVERSEAS_PROVIDER_FILTER));
      return;
    }

    dispatch(fetchFilterAsync.request());
    try {
      const response = await getFilterAPI(apiClient, isPlanList);
      dispatch(fetchFilterAsync.success(response));
    } catch (error) {
      logger.error(error);
      dispatch(fetchFilterAsync.failure(error));
    }
  };
